import { COMMA_SEPARATED_ATTRIBUTES } from './filterQueryGenerator';

export const getInputType = (key, operator, filterTypes) => {
  if (key === 'created_at' || key === 'last_activity_at')
    if (['newer_than_days', 'older_than_days'].includes(operator))
      return 'plain_text';
  const type = filterTypes.find(filter => filter.attributeKey === key);
  return type?.inputType;
};

export const generateCustomAttributesInputType = type => {
  const filterInputTypes = {
    text: 'string',
    number: 'string',
    date: 'string',
    checkbox: 'multi_select',
    list: 'multi_select',
    link: 'string',
  };
  return filterInputTypes[type];
};

export const getAttributeInputType = (key, allCustomAttributes) => {
  const customAttribute = allCustomAttributes.find(
    attr => attr.attribute_key === key
  );

  // A saved filter can name an attribute that is neither a known standard one nor a
  // custom attribute (a custom attribute deleted since, say). Fall back instead of
  // throwing, so one stale condition cannot take down the whole edit modal.
  if (!customAttribute) return undefined;

  const { attribute_display_type } = customAttribute;
  return generateCustomAttributesInputType(attribute_display_type);
};

export const getValuesName = (values, list, idKey, nameKey) => {
  const item = list?.find(v => v[idKey] === values[0]);
  return {
    id: values[0],
    name: item ? item[nameKey] : values[0],
  };
};

export const getValuesForStatus = values => {
  return values.map(value => ({ id: value, name: value }));
};

const getValuesForLabels = (values, labels) => {
  const selectedLabels = labels.filter(label => values.includes(label.title));
  return selectedLabels.map(({ title }) => ({
    id: title,
    name: title,
  }));
};

const getValuesForInboxes = (values, inboxes) => {
  return inboxes.filter(inbox => values.includes(inbox.id));
};

const getValuesForTeams = (values, teams) => {
  return teams.filter(team => values.includes(team.id));
};

const getValuesForAssignees = (values, agents) => {
  const candidates = [{ id: 'current_user' }, ...agents];
  return candidates.filter(agent => values.includes(agent.id));
};

const getValuesForLanguages = (values, languages) => {
  const selectedLanguages = languages.filter(language =>
    values.includes(language.id)
  );
  return selectedLanguages.map(({ id, name }) => ({
    id: id.toLowerCase(),
    name: name,
  }));
};

const getValuesForCountries = (values, countries) => {
  const selectedCountries = countries.filter(country =>
    values.includes(country.id)
  );
  return selectedCountries.map(({ id, name }) => ({
    id: id,
    name: name,
  }));
};

const getValuesForPriority = (values, priority) => {
  return priority.filter(option => values.includes(option.id));
};

export const getValuesForFilter = (filter, params) => {
  const { attribute_key, values } = filter;
  const {
    languages,
    countries,
    agents,
    inboxes,
    teams,
    campaigns,
    labels,
    priority,
  } = params;
  switch (attribute_key) {
    case 'status':
      return getValuesForStatus(values);
    case 'assignee_id':
      return getValuesForAssignees(values, agents, 'id', 'name');
    case 'inbox_id':
      return getValuesForInboxes(values, inboxes, 'id', 'name');
    case 'team_id':
      return getValuesForTeams(values, teams);
    case 'campaign_id':
      return getValuesName(values, campaigns, 'id', 'title');
    case 'labels':
      return getValuesForLabels(values, labels);
    case 'priority':
      return getValuesForPriority(values, priority);
    case 'browser_language':
      return getValuesForLanguages(values, languages);
    case 'country_code':
      return getValuesForCountries(values, countries);
    case 'phone_call_type':
      return getValuesForStatus(values);
    case 'rating':
      return getValuesForStatus(values);
    default:
      return { id: values[0], name: values[0] };
  }
};

// A comma separated attribute is stored as a list but edited in a single text box,
// so it has to come back joined. Returning values[0] here used to silently drop
// every value after the first on edit-and-resave.
const generateValueForPlainText = (attribute_key, values) =>
  COMMA_SEPARATED_ATTRIBUTES.includes(attribute_key)
    ? values.join(',')
    : values[0].toString();

export const generateValuesForEditCustomViews = (filter, params) => {
  const { attribute_key, filter_operator, values } = filter;
  const { filterTypes, allCustomAttributes } = params;
  const inputType = getInputType(attribute_key, filter_operator, filterTypes);

  if (inputType === undefined) {
    const filterInputTypes = getAttributeInputType(
      attribute_key,
      allCustomAttributes
    );
    return filterInputTypes === 'string'
      ? values[0].toString()
      : { id: values[0], name: values[0] };
  }

  return inputType === 'multi_select' || inputType === 'search_select'
    ? getValuesForFilter(filter, params)
    : generateValueForPlainText(attribute_key, values);
};
